/**
 * Admin → Market Data (Zillow ZHVI).
 *
 * Backend: `/admin/data/zhvi/*` (app/routers/admin_data.py), spec in
 * frontend_documentation/admin-market-data-zhvi.md. The dataset is versioned and
 * SHARED across both portals — one upload serves Eastview and LendMarq.
 *
 * Three panels:
 *   1. Upload — the zip-level ZHVI CSV (~120MB, 30–60s parse). `lastReportedDate`
 *      on the response is the data's as-of month and is rendered prominently.
 *   2. Activity ledger — the user-stamped version history with the active row
 *      highlighted, a second-user acknowledge action, and a rollback (one level;
 *      calling it again rolls forward — 409 when there is no prior generation).
 *   3. Zip lookup — the per-zip record + MarketAssessment the opening agent uses,
 *      with the 24-month time series charted.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import UndoRounded from "@mui/icons-material/UndoRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { useSnackbar } from "notistack";

import { AdminGrid } from "../shared/adminGrid";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminZhviApi } from "../../../api/adminDataApi";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import {
  AdminCard,
  AdminEmpty,
  AdminScreenShell,
} from "../shared/AdminScreenShell";
import ZhviZipPanel from "./ZhviZipPanel";

const STATUS_COLORS = {
  active: { bg: "#dcfce7", fg: "#166534" },
  superseded: { bg: "#f1f5f9", fg: "#64748b" },
  rolled_back: { bg: "#fee2e2", fg: "#991b1b" },
};

const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

export default function AdminMarketDataScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [lastUpload, setLastUpload] = useState(null);

  const fetchVersions = useCallback(() => {
    setLoading(true);
    adminZhviApi
      .versions()
      .then((rows) => setVersions(rows))
      .catch((err) => {
        setVersions([]);
        enqueueSnackbar(errText(err, "Couldn't load the ZHVI version ledger"), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleFilePicked = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      // Let the same file be re-picked after a failed upload.
      event.target.value = "";
      if (!file) return;
      setUploading(true);
      setUploadPct(0);
      adminZhviApi
        .upload(file, (p) => {
          if (p.total) setUploadPct(Math.round((p.loaded / p.total) * 100));
        })
        .then((result) => {
          setLastUpload(result);
          enqueueSnackbar(
            `ZHVI v${result?.version} ingested — ${(
              result?.rows || 0
            ).toLocaleString()} rows`,
            { variant: "success" },
          );
          fetchVersions();
        })
        .catch((err) =>
          enqueueSnackbar(errText(err, "ZHVI upload failed"), {
            variant: "error",
          }),
        )
        .finally(() => {
          setUploading(false);
          setUploadPct(0);
        });
    },
    [enqueueSnackbar, fetchVersions],
  );

  const handleAcknowledge = useCallback(
    (row) => {
      adminZhviApi
        .acknowledge(row.versionId)
        .then((res) => {
          enqueueSnackbar("Version acknowledged", { variant: "success" });
          setVersions((prev) =>
            (prev || []).map((r) =>
              r.versionId === row.versionId
                ? {
                    ...r,
                    acknowledgedBy: res?.acknowledgedBy,
                    acknowledgedAt: new Date().toISOString(),
                  }
                : r,
            ),
          );
        })
        .catch((err) =>
          enqueueSnackbar(errText(err, "Couldn't acknowledge version"), {
            variant: "error",
          }),
        );
    },
    [enqueueSnackbar],
  );

  const handleRollback = useCallback(() => {
    if (
      !window.confirm(
        "Roll the ZHVI dataset back to the prior generation? This swaps the active data for every consumer of the market checks.",
      )
    )
      return;
    setLoading(true);
    adminZhviApi
      .rollback()
      .then((res) => {
        enqueueSnackbar(
          `Rolled back to v${res?.version ?? "prior"}`,
          { variant: "success" },
        );
        fetchVersions();
      })
      .catch((err) =>
        // 409 = no prior generation to roll back to.
        enqueueSnackbar(errText(err, "Rollback failed"), { variant: "error" }),
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar, fetchVersions]);

  const activeVersion = useMemo(
    () => (versions || []).find((v) => v.status === "active"),
    [versions],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Version",
        field: "version",
        width: 100,
        sort: "desc",
        valueFormatter: (p) => (p.value == null ? "—" : `v${p.value}`),
      },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        cellRenderer: (p) => {
          const c = STATUS_COLORS[p.value] || STATUS_COLORS.superseded;
          return (
            <Chip
              size="small"
              label={String(p.value || "—").replace("_", " ")}
              sx={{
                bgcolor: c.bg,
                color: c.fg,
                fontWeight: 600,
                fontSize: 11,
                height: 20,
              }}
            />
          );
        },
      },
      { headerName: "Action", field: "action", width: 100 },
      {
        headerName: "As-of (last reported)",
        field: "lastReportedDate",
        width: 175,
        // The data's as-of month — a date-only value, so format it without a
        // timezone shift.
        valueFormatter: (p) => (p.value ? formatDate(p.value) : "—"),
      },
      {
        headerName: "Rows",
        field: "rowCount",
        width: 110,
        valueFormatter: (p) =>
          p.value == null ? "—" : Number(p.value).toLocaleString(),
      },
      {
        headerName: "Source File",
        field: "sourceFile",
        minWidth: 200,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      { headerName: "Uploaded By", field: "uploadedBy", minWidth: 180, flex: 1 },
      {
        headerName: "Uploaded",
        field: "uploadedAt",
        width: 165,
        valueFormatter: (p) => formatDateTime(p.value) || "—",
      },
      {
        headerName: "Acknowledged",
        colId: "acknowledged",
        minWidth: 200,
        flex: 1,
        cellRenderer: (p) =>
          p.data?.acknowledgedBy ? (
            <Txt sx={{ fontSize: 12, color: "#166534" }}>
              Acknowledged by {p.data.acknowledgedBy}
            </Txt>
          ) : (
            <Button
              size="small"
              variant="text"
              startIcon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
              onClick={() => handleAcknowledge(p.data)}
            >
              Acknowledge
            </Button>
          ),
      },
      {
        headerName: "Rolled Back By",
        field: "rolledBackBy",
        width: 170,
        valueFormatter: (p) => p.value || "—",
      },
    ],
    [handleAcknowledge],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    }),
    [],
  );

  const getRowId = useCallback((p) => String(p.data?.versionId), []);
  const getRowClass = useCallback(
    (p) => (p.data?.status === "active" ? "active" : undefined),
    [],
  );

  return (
    <AdminScreenShell
      icon={<InsightsOutlined />}
      title="Market Data"
      loading={loading}
      subtitle="Versioned Zillow ZHVI reference data. The active dataset is shared across both portals and feeds the Loan Opening agent's per-zip market checks (median value, HPA, trend, rural signal)."
      actions={
        <>
          <Tooltip title="Refresh ledger">
            <span>
              <IconButton onClick={fetchVersions} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<UndoRounded />}
            onClick={handleRollback}
            disabled={loading || uploading || !versions?.length}
          >
            Roll back
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "Upload ZHVI CSV"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFilePicked}
          />
        </>
      }
    >
      {uploading ? (
        <AdminCard title="Upload in progress">
          <Txt sx={{ fontSize: 12.5, color: "#64748b", display: "block", mb: 1 }}>
            {uploadPct < 100
              ? `Transferring the workbook — ${uploadPct}%`
              : "Transferred. Parsing server-side (30–60s for a ~120MB file) — don't navigate away."}
          </Txt>
          <LinearProgress
            variant={uploadPct < 100 ? "determinate" : "indeterminate"}
            value={uploadPct}
          />
        </AdminCard>
      ) : null}

      {lastUpload ? (
        <AdminCard title="Last upload">
          <Flx g={4} wrap>
            <Stat label="Version" value={`v${lastUpload.version}`} />
            <Stat
              label="Rows"
              value={Number(lastUpload.rows || 0).toLocaleString()}
            />
            <Stat
              label="Last reported date (as-of)"
              value={
                lastUpload.lastReportedDate
                  ? formatDate(lastUpload.lastReportedDate)
                  : "—"
              }
              emphasis
            />
            <Stat label="Uploaded by" value={lastUpload.uploadedBy || "—"} />
          </Flx>
        </AdminCard>
      ) : null}

      <AdminCard
        title="Activity ledger"
        endContent={
          activeVersion ? (
            <Txt sx={{ fontSize: 12, color: "#64748b" }}>
              Active: v{activeVersion.version} · as-of{" "}
              {activeVersion.lastReportedDate
                ? formatDate(activeVersion.lastReportedDate)
                : "—"}
            </Txt>
          ) : null
        }
        dense
      >
        {versions && versions.length === 0 ? (
          <AdminEmpty>
            No ZHVI versions yet — upload the Zillow zip-level CSV to seed the
            dataset.
          </AdminEmpty>
        ) : (
          <AdminGrid rowCount={(versions || []).length} maxRows={10}>
            <AgGridReact
              rowData={versions || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              getRowClass={getRowClass}
              loading={loading}
            />
          </AdminGrid>
        )}
      </AdminCard>

      <ZhviZipPanel />
    </AdminScreenShell>
  );
}

const Stat = ({ label, value, emphasis }) => (
  <Flx column g={0.25}>
    <Txt sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{label}</Txt>
    <Txt
      sx={{
        fontSize: emphasis ? 18 : 14,
        fontWeight: emphasis ? 800 : 600,
        color: "#0f172a",
      }}
    >
      {value}
    </Txt>
  </Flx>
);
